import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

export default function ContactForm() {
  const [searchParams] = useSearchParams();
  const productParam = searchParams.get("produkt");

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    description: "",
    website_url: "", // Honeypot field
  });

  const [status, setStatus] = useState({
    type: null, // 'success' | 'error' | 'submitting'
    message: "",
  });

  // Pre-fill message if product is specified in URL
  useEffect(() => {
    if (productParam) {
      setFormData((prev) => ({
        ...prev,
        description: `Hej Havneloppen,\n\nJeg er interesseret i jeres vare "${productParam}" og vil gerne høre mere om den (f.eks. om den stadig er tilgængelig, eller hvornår jeg kan komme og se den).\n\nMvh. `,
      }));
    }
  }, [productParam]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim() || !formData.description.trim()) {
      setStatus({
        type: "error",
        message: "Venligst udfyld alle påkrævede felter.",
      });
      return;
    }

    setStatus({ type: "submitting", message: "Sender din besked..." });

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          description: formData.description,
          website_url: formData.website_url, // Honeypot
        }),
      });

      const result = await response.json();

      if (response.ok && result.status === "ok") {
        setStatus({
          type: "success",
          message: "Mange tak for din besked! Vi vender tilbage til dig så hurtigt som muligt.",
        });
        // Reset form (except honeypot)
        setFormData({
          name: "",
          phone: "",
          email: "",
          description: "",
          website_url: "",
        });
      } else {
        setStatus({
          type: "error",
          message: result.message || "Der skete en fejl under afsendelsen. Prøv venligst igen.",
        });
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setStatus({
        type: "error",
        message: "Kunne ikke oprette forbindelse til serveren. Tjek din internetforbindelse og prøv igen.",
      });
    }
  };

  return (
    <div className="rounded-2xl border border-warm-beige bg-white p-6 shadow-sm md:p-8">
      <h3 className="font-display text-xl font-bold text-brand-blue mb-4">
        Send os en besked
      </h3>
      <p className="text-sm text-dark-graphite-light mb-6">
        Har du spørgsmål til en vare, eller vil du høre om vi kan købe dine loppefund? Udfyld formularen nedenfor, så svarer vi hurtigst muligt.
      </p>

      {status.type === "success" ? (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-5 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 mb-3">
            <i className="fa-solid fa-circle-check text-xl" />
          </div>
          <h4 className="font-display text-lg font-bold text-emerald-900 mb-1">Beskeden er sendt!</h4>
          <p className="text-sm text-emerald-800">{status.message}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Honeypot Field - ABSOLUTELY HIDDEN from humans */}
          <div className="sr-only absolute -z-10 overflow-hidden h-0 w-0" aria-hidden="true">
            <label htmlFor="website_url">Do not fill this field if you are human</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              placeholder="http://example.com"
            />
          </div>

          {/* Name Field */}
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-dark-graphite mb-1.5">
              Navn <span className="text-brand-orange">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              className="w-full rounded-lg border border-warm-beige bg-warm-white/30 px-4 py-2.5 text-sm text-dark-graphite placeholder-dark-graphite/40 transition-all focus:border-brand-orange focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-orange/20"
              placeholder="Dit fulde navn"
            />
          </div>

          {/* Grid for Phone & Email */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {/* Phone Field */}
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-dark-graphite mb-1.5">
                Telefonnummer <span className="text-brand-orange">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                className="w-full rounded-lg border border-warm-beige bg-warm-white/30 px-4 py-2.5 text-sm text-dark-graphite placeholder-dark-graphite/40 transition-all focus:border-brand-orange focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-orange/20"
                placeholder="F.eks. 28 76 79 15"
              />
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-dark-graphite mb-1.5">
                E-mailadresse <span className="text-brand-orange">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full rounded-lg border border-warm-beige bg-warm-white/30 px-4 py-2.5 text-sm text-dark-graphite placeholder-dark-graphite/40 transition-all focus:border-brand-orange focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-orange/20"
                placeholder="din@email.dk"
              />
            </div>
          </div>

          {/* Message / Description Field */}
          <div>
            <label htmlFor="description" className="block text-sm font-semibold text-dark-graphite mb-1.5">
              Besked <span className="text-brand-orange">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              required
              rows="5"
              value={formData.description}
              onChange={handleChange}
              className="w-full rounded-lg border border-warm-beige bg-warm-white/30 px-4 py-2.5 text-sm text-dark-graphite placeholder-dark-graphite/40 transition-all focus:border-brand-orange focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-orange/20 resize-y"
              placeholder="Skriv din besked her..."
            />
          </div>

          {/* Error Message */}
          {status.type === "error" && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 p-3.5 text-sm text-rose-800 flex items-center gap-2">
              <i className="fa-solid fa-triangle-exclamation text-base" />
              <span>{status.message}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status.type === "submitting"}
            className="w-full rounded-lg bg-brand-orange px-6 py-3 text-sm font-bold text-warm-white shadow-sm transition-all hover:bg-brand-orange-hover focus:outline-none focus:ring-2 focus:ring-brand-orange/50 disabled:bg-brand-orange/50 disabled:cursor-not-allowed"
          >
            {status.type === "submitting" ? (
              <span className="flex items-center justify-center gap-2">
                <i className="fa-solid fa-spinner animate-spin" />
                Sender besked...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <i className="fa-solid fa-paper-plane" />
                Send besked
              </span>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
